import { useState } from 'react'
import { 
  Trophy, 
  Crown,
  Star,
  TrendingUp,

  Ship,
  Target
} from 'lucide-react'

interface RankingEntry {
  id: string
  rank: number
  name: string
  faction: string
  score: number
  level: number
  change: number
  ships: number
  victories: number
}

const Rankings = () => {
  const [rankings] = useState<RankingEntry[]>([
    {
      id: '1',
      rank: 1,
      name: 'Emperor Kaz\'thul',
      faction: 'Zephyrian Empire',
      score: 125000,
      level: 42,
      change: 2,
      ships: 234,
      victories: 89
    },
    {
      id: '2',
      rank: 2,
      name: 'Admiral Vex',
      faction: 'Terran Federation',
      score: 118500,
      level: 38,
      change: -1,
      ships: 198,
      victories: 76
    },
    {
      id: '3',
      rank: 3,
      name: 'Commander Nova',
      faction: 'Solaris Union',
      score: 112000,
      level: 35,
      change: 1,
      ships: 167,
      victories: 64
    }
  ])

  const [activeTab, setActiveTab] = useState('overall')

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <Trophy className="w-10 h-10 mr-3 text-yellow-400" />
              Galactic Rankings
            </h1>
            <p className="text-gray-400">See where you stand among the galaxy's greatest commanders</p>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['overall', 'military', 'economic', 'exploration'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-purple-600 text-white'
                  : 'bg-slate-800/50 text-gray-400 hover:bg-slate-700/50'
              }`}
            >
              {tab} Rankings
            </button>
          ))}
        </div>

        {/* Rankings List */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
          <div className="p-6 border-b border-white/10">
            <div className="grid grid-cols-12 gap-4 text-sm font-semibold text-gray-400">
              <div className="col-span-1">Rank</div>
              <div className="col-span-3">Commander</div>
              <div className="col-span-2">Faction</div>
              <div className="col-span-2">Score</div>
              <div className="col-span-1">Level</div>
              <div className="col-span-1">Ships</div>
              <div className="col-span-1">Victories</div>
              <div className="col-span-1">Change</div>
            </div>
          </div>
          
          <div className="divide-y divide-white/10">
            {rankings.map((entry) => (
              <div key={entry.id} className="p-6 hover:bg-slate-700/30 transition-colors">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-1 flex items-center">
                    {entry.rank <= 3 ? (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        entry.rank === 1 ? 'bg-yellow-500' :
                        entry.rank === 2 ? 'bg-gray-400' :
                        'bg-orange-500'
                      }`}>
                        <Crown className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="text-2xl font-bold text-gray-400">
                        {entry.rank}
                      </div>
                    )}
                  </div>
                  
                  <div className="col-span-3">
                    <div className="font-semibold text-white">{entry.name}</div>
                  </div>
                  
                  <div className="col-span-2">
                    <div className="text-gray-300">{entry.faction}</div>
                  </div>
                  
                  <div className="col-span-2">
                    <div className="font-semibold text-purple-400">
                      {entry.score.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="col-span-1">
                    <div className="text-blue-400">{entry.level}</div>
                  </div>
                  
                  <div className="col-span-1">
                    <div className="text-gray-300 flex items-center">
                      <Ship className="w-3 h-3 mr-1" />
                      {entry.ships}
                    </div>
                  </div>
                  
                  <div className="col-span-1">
                    <div className="text-gray-300 flex items-center">
                      <Target className="w-3 h-3 mr-1" />
                      {entry.victories}
                    </div>
                  </div>
                  
                  <div className="col-span-1">
                    <div className={`flex items-center text-sm ${
                      entry.change > 0 ? 'text-green-400' :
                      entry.change < 0 ? 'text-red-400' :
                      'text-gray-400'
                    }`}>
                      {entry.change > 0 && <TrendingUp className="w-3 h-3 mr-1" />}
                      {entry.change < 0 && <TrendingUp className="w-3 h-3 mr-1 rotate-180" />}
                      {entry.change !== 0 && Math.abs(entry.change)}
                      {entry.change === 0 && '-'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Player's Current Rank */}
        <div className="mt-6 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-lg p-6 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-white">Your Current Rank</div>
                <div className="text-gray-400">Space Commander • Terran Federation</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-purple-400">#15</div>
              <div className="text-sm text-gray-400">Out of 1,247 players</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Rankings